import { deviceLocal } from '@/privacy/deviceStorage';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { THEME_STORAGE_KEY } from './themeStorage';

export type ThemeMode = 'system' | 'light' | 'dark';
type ResolvedTheme = 'light' | 'dark';

interface ThemeContextValue {
  mode: ThemeMode;
  resolvedTheme: ResolvedTheme;
  setMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStoredMode(): ThemeMode {
  if (typeof window === 'undefined') return 'system';
  const stored = deviceLocal.getItem(THEME_STORAGE_KEY);
  return stored === 'light' || stored === 'dark' || stored === 'system' ? stored : 'system';
}

function systemPrefersDark(): boolean {
  if (typeof window === 'undefined') return false;
  if (!window.matchMedia) return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/**
 * I-1421: a scheme switched under a drawn page is drawn in the new colours in its first frame, every
 * surface at once. A control with `transition-colors` (the phone top row's scope and Filter, the
 * chips, the search field) otherwise faded from the old scheme's colours for 150 ms while the page
 * around it had already switched: dark text on the dark page, light chips, for 10–18 frames.
 *
 * Transitions are off while the class flips, the new colours are committed by a forced style and
 * layout pass, and transitions come back a frame later, so the hover and press fades are untouched.
 */
function withoutTransitions(change: () => void): void {
  const pause = document.createElement('style');
  pause.dataset.themeSwitch = '';
  pause.textContent = '*,*::before,*::after{transition:none!important}';
  document.head.appendChild(pause);
  change();
  void document.body.offsetHeight;
  requestAnimationFrame(() => requestAnimationFrame(() => pause.remove()));
}

function applyResolvedTheme(theme: ResolvedTheme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  // The first apply only confirms what `index.html`'s script drew; there is nothing to fade from.
  if (root.dataset.theme && root.dataset.theme !== theme && document.body) {
    withoutTransitions(() => setThemeClasses(theme));
  } else {
    setThemeClasses(theme);
  }
  applyTitleBarColour();
}

function setThemeClasses(theme: ResolvedTheme): void {
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
  root.dataset.theme = theme;
  root.style.colorScheme = theme;
  document.body.classList.remove('dark');
  document.body.dataset.theme = theme;
  document.body.style.colorScheme = theme;
}

/**
 * T-0328: an installed web app's title bar is the page's `theme-color`, and it follows the theme
 * the person chose, not only the system's. `index.html` states one per system scheme for the first
 * paint; once the theme is resolved, every one of them names this theme's canvas, so whichever the
 * browser matches is right — Light chosen on a dark system included.
 *
 * ⭐ Read from the stylesheet's `--evx-canvas`, after the class above has switched it, so the colour
 * has one home. Unread (no stylesheet, as in jsdom) leaves the metas as they are.
 */
function applyTitleBarColour(): void {
  const canvas = getComputedStyle(document.documentElement).getPropertyValue('--evx-canvas').trim();
  if (!canvas) return;
  const metas = document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]');
  if (metas.length === 0) {
    const meta = document.createElement('meta');
    meta.name = 'theme-color';
    meta.content = canvas;
    document.head.appendChild(meta);
    return;
  }
  metas.forEach((meta) => {
    meta.content = canvas;
  });
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(() => readStoredMode());
  const [systemDark, setSystemDark] = useState(() => systemPrefersDark());

  const resolvedTheme: ResolvedTheme = mode === 'system' ? (systemDark ? 'dark' : 'light') : mode;

  useEffect(() => {
    applyResolvedTheme(resolvedTheme);
  }, [resolvedTheme]);

  useEffect(() => {
    deviceLocal.setItem(THEME_STORAGE_KEY, mode);
  }, [mode]);

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => setSystemDark(query.matches);
    handleChange();
    query.addEventListener('change', handleChange);
    return () => query.removeEventListener('change', handleChange);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      mode,
      resolvedTheme,
      setMode: setModeState,
    }),
    [mode, resolvedTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('useTheme must be used inside ThemeProvider');
  return value;
}
